import NeptuneElement from '../../NeptuneElement.js';
import SideNavItem from './sidenavItem/SidenavItem.js';

class Sidenav extends NeptuneElement {    

    static SIDENAV_STATES = {
        rail: 'rail',
        primaryDrawer: 'primary-drawer',
        secondaryDrawer: 'secondary-drawer'
    };

    templateMarkup = `
        <nav>
            <button class="font-size-md">
                <i class="fa-solid fa-chevron-right"></i>
                <span>Collapse</span>
            </button>            
            <button class="font-size-md" hidden>
                <i class="fa-solid fa-chevron-left"></i>
                <span>Back</span>
            </button>
            <menu></menu>
            <menu hidden></menu>
            <slot name="bottom-menu"></slot>
        </nav>
    `;

    componentCssUrl = '/src/components/chrome/sidenav/sidenav.css';

    menuData = [];

    #activeParentLabel = null;

    #activeRoute = null;

    async connectedCallback() {
        await super.connectedCallback();
        this.#renderPrimaryMenu();
        const state = this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail;
        const nav = this.shadowRoot.querySelector('nav');
        if (nav) nav.dataset.state = state;
        this.#updateNavItemsCollapsedState(state === Sidenav.SIDENAV_STATES.rail);
        
        document.dispatchEvent(new CustomEvent('sidenav-ready', {
            detail: { sidenavElement: this }
        }));
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();
        
        document.dispatchEvent(new CustomEvent('sidenav-removed', {
            detail: { sidenavElement: this }
        }));
    }

    static get observedAttributes() {
        return ['state'];
    }

    async setEventListeners() {
        this.toggleButton = this.shadowRoot.querySelector('nav > button:first-of-type');
        this.backButton = this.shadowRoot.querySelector('nav > button:nth-of-type(2)');

        this.handleToggleBound = this.#handleToggleClick.bind(this);
        this.handleGoBackBound = this.#goBackToDrawer.bind(this);
        this.handleItemToggleBound = this.#handleItemToggle.bind(this);
        this.handleItemClickBound = this.#handleItemClick.bind(this);
        this.handleDrawerOpenBound = this.#handleDrawerOpen.bind(this);
        this.handleLocationChangeBound = this.#handleLocationChange.bind(this);

        this.toggleButton?.addEventListener('click', this.handleToggleBound);
        this.backButton?.addEventListener('click', this.handleGoBackBound);
        this.addEventListener('neptune-sidenav-item-toggle', this.handleItemToggleBound);
        this.addEventListener('neptune-sidenav-item-click', this.handleItemClickBound);
        document.addEventListener('neptune-drawer-open', this.handleDrawerOpenBound);

        this.patchHistoryNavigation();

        window.addEventListener('neptune-location-change', this.handleLocationChangeBound);
        window.addEventListener('popstate', this.handleLocationChangeBound);
    }

    async removeEventListeners() {
        this.toggleButton?.removeEventListener('click', this.handleToggleBound);
        this.backButton?.removeEventListener('click', this.handleGoBackBound);
        this.removeEventListener('neptune-sidenav-item-toggle', this.handleItemToggleBound);
        this.removeEventListener('neptune-sidenav-item-click', this.handleItemClickBound);
        document.removeEventListener('neptune-drawer-open', this.handleDrawerOpenBound);
        window.removeEventListener('neptune-location-change', this.handleLocationChangeBound);
        window.removeEventListener('popstate', this.handleLocationChangeBound);

        this.toggleButton = null;
        this.backButton = null;
        this.handleToggleBound = null;
        this.handleGoBackBound = null;
        this.handleItemToggleBound = null;
        this.handleItemClickBound = null;
        this.handleDrawerOpenBound = null;
        this.handleLocationChangeBound = null;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    /**
     * Processes attribute changes and updates the component state accordingly.
     * @param {string} name - The attribute name
     * @param {string} oldValue - The previous attribute value
     * @param {string} newValue - The new attribute value
     */
    processAttribute(name, oldValue, newValue) {
        switch (name) {
            case 'state': {
                if (!this.shadowRoot) return;

                const nav = this.shadowRoot.querySelector('nav');
                if (!nav) return;

                const state = newValue || Sidenav.SIDENAV_STATES.rail;
                nav.dataset.state = state;
                document.body.setAttribute('data-sidenav-state', state);

                const isRail = state === Sidenav.SIDENAV_STATES.rail;
                const isSecond = state === Sidenav.SIDENAV_STATES.secondaryDrawer;

                this.#updateToggleButton(isRail, isSecond);
                this.#updateBackButton(isSecond);
                this.#updateMenuVisibility(isSecond);
                this.#updateNavItemsCollapsedState(isRail);
                if (!isSecond) {
                    this.shadowRoot.querySelectorAll('neptune-sidenav-item[expanded]')
                        .forEach(element => element.removeAttribute('expanded'));
                    this.#collapseBottomMenuItems();
                }
                break;
            }
            default: {
                break;
            }
        }
    }

    /**
     * Retrieves all sidenav items from both primary menu and slotted bottom menu.
     * @returns {HTMLElement[]}
     */
    #allItems() {
        const slot = this.shadowRoot?.querySelector('slot[name="bottom-menu"]');
        const slotted = slot?.assignedElements().flatMap(el => [...el.querySelectorAll('neptune-sidenav-item')]) ?? [];
        return [...(this.shadowRoot?.querySelectorAll('neptune-sidenav-item') ?? []), ...slotted];
    }

    /**
     * Clears the active state from all navigation items.
     */
    clearActive() {
        this.#activeRoute = null;
        this.#allItems().forEach(el => { el.removeAttribute('active'); el.setAttribute('state', 'default'); });
    }

    /**
     * Normalizes a route by removing a leading hash character if present.
     * Allows route matching to work with both hash (#/route) and path (/route) formats.
     * @param {string} route - The route path to normalize
     * @returns {string}
     */
    #normalizeRoute(route) {
        return route?.replace(/^#/, '') ?? '';
    }

    /**
     * Sets the active route and highlights the matching navigation item(s).
     * If the route matches a submenu item, the parent item is also highlighted.
     * @param {string} route - The route path to activate
     */
    setActiveRoute(route) {
        if (!route) { this.clearActive(); return; }
        this.#activeRoute = route;
        const normalized = this.#normalizeRoute(route);

        this.#allItems().forEach(navItem => {
            const match = this.#normalizeRoute(navItem.getAttribute('route')) === normalized;
            navItem.toggleAttribute('active', match);
            navItem.setAttribute('state', match ? 'active' : 'default');
        });

        const parentInMenu = this.menuData.flatMap(group => group.menu || [group])
            .find(menuItem => menuItem.subMenu?.some(subItem => this.#normalizeRoute(subItem.route) === normalized));
        if (parentInMenu) {
            this.#activeParentLabel = parentInMenu.label || parentInMenu.name;
            const parentEl = this.shadowRoot?.querySelector(`menu:first-of-type neptune-sidenav-item[label="${this.#activeParentLabel}"]`);
            if (parentEl) { parentEl.setAttribute('active', ''); parentEl.setAttribute('state', 'active'); }
        } else {
            const slot = this.shadowRoot?.querySelector('slot[name="bottom-menu"]');
            slot?.assignedElements()
                .flatMap(assignedEl => [...assignedEl.querySelectorAll('neptune-sidenav-item[data-submenu]')])
                .forEach(bottomMenuItem => {
                    const submenuItems = JSON.parse(bottomMenuItem.dataset.submenu);
                    if (submenuItems.some(subItem => this.#normalizeRoute(subItem.route) === normalized)) {
                        bottomMenuItem.setAttribute('active', '');
                        bottomMenuItem.setAttribute('state', 'active');
                        this.#activeParentLabel = bottomMenuItem.getAttribute('label');
                    }
                });
        }
    }

    /**
     * Updates the navigation menu data and re-renders if the component is already rendered.
     * Skips re-render if the new data is identical to the existing menu data.
     * @param {Array} data - Array of menu items or groups
     */
    setMenuData(data) {
        const normalized = Array.isArray(data) ? JSON.parse(JSON.stringify(data)) : [];
        if (JSON.stringify(this.menuData) === JSON.stringify(normalized)) return;
        this.menuData = normalized;
        if (this.isRendered) {
            this.#renderPrimaryMenu();
        }
    }

    /**
     * Updates the toggle button icon and label based on the current sidenav state.
     * @param {boolean} isRail - Whether the sidenav is in rail state
     * @param {boolean} isSecond - Whether the sidenav is in secondary drawer state
     */
    #updateToggleButton(isRail, isSecond) {
        const toggleBtn = this.shadowRoot.querySelector('nav > button:first-of-type');
        if (!toggleBtn) return;

        const toggleIcon = toggleBtn.querySelector('i');
        const toggleLabel = toggleBtn.querySelector('span');

        if (toggleIcon) {
            toggleIcon.className = `fa-solid fa-chevron-${isRail ? 'right' : 'left'}`;
        }
        if (toggleLabel) {
            toggleLabel.textContent = isRail ? 'Expand' : 'Collapse';
        }
        toggleBtn.hidden = isSecond;
    }

    /**
     * Updates the back button visibility based on whether the sidenav is in secondary drawer state.
     * @param {boolean} isSecond - Whether the sidenav is in secondary drawer state
     */
    #updateBackButton(isSecond) {
        const backBtn = this.shadowRoot.querySelector('nav > button:nth-of-type(2)');
        if (!backBtn) return;
        backBtn.hidden = !isSecond;
    }

    /**
     * Updates the visibility of primary and secondary menus based on the current state.
     * @param {boolean} isSecond - Whether the sidenav is in secondary drawer state
     */
    #updateMenuVisibility(isSecond) {
        const primaryMenu = this.shadowRoot.querySelector('menu:first-of-type');
        const secondaryMenu = this.shadowRoot.querySelector('menu:nth-of-type(2)');

        if (primaryMenu) {
            primaryMenu.hidden = isSecond;
        }
        if (secondaryMenu) {
            secondaryMenu.hidden = !isSecond;
        }
    }

    /**
     * Updates the bottom menu (slot) visibility based on navigation state and collapsed state.
     * @param {boolean} collapsed - Whether the navigation is in collapsed state
     */
    #updateBottomMenuVisibility(collapsed) {
        const bottomMenu = this.shadowRoot?.querySelector('slot[name="bottom-menu"]');
        if (!bottomMenu) return;
        const state = this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail;
        const isSecond = state === Sidenav.SIDENAV_STATES.secondaryDrawer;
        bottomMenu.hidden = isSecond;
    }

    /**
     * Updates the collapsed state of all primary and slotted navigation items.
     * @param {boolean} collapsed - Whether items should be collapsed
     */
    #updateNavItemsCollapsedState(collapsed) {
        const primaryMenuItems = this.shadowRoot.querySelectorAll('menu neptune-sidenav-item');
        const bottomMenuItems = this.querySelectorAll('[slot="bottom-menu"] neptune-sidenav-item');
        [...primaryMenuItems, ...bottomMenuItems].forEach(el => {
            if (collapsed) {
                el.setAttribute('collapsed', 'true');
            } else {
                el.removeAttribute('collapsed');
            }
        });
        this.#updateBottomMenuVisibility(collapsed);
    }

    /**
     * Handles toggle button click to switch between rail and primary-drawer states.
     */
    #handleToggleClick() {
        const current = this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail;
        const next = current === Sidenav.SIDENAV_STATES.rail
            ? Sidenav.SIDENAV_STATES.primaryDrawer
            : Sidenav.SIDENAV_STATES.rail;

        this.setAttribute('state', next);
        this.#emitToggle(next);
    }

    /**
     * Handles item toggle events to expand/collapse submenus or ensure drawer state.
     * @param {CustomEvent} event - The neptune-sidenav-item-toggle event
     */
    #handleItemToggle(event) {
        const { expanded, label } = event.detail;
        if (expanded) {
            const isRail = (this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail) === Sidenav.SIDENAV_STATES.rail;
            if (isRail) {
                this.shadowRoot.querySelectorAll('neptune-sidenav-item[expanded]')
                    .forEach(el => el.removeAttribute('expanded'));
                this.#collapseBottomMenuItems();
                this.setAttribute('state', Sidenav.SIDENAV_STATES.primaryDrawer);
                this.#emitToggle(Sidenav.SIDENAV_STATES.primaryDrawer);
                return;
            }
            const item = this.#findMenuItem(label);
            if (item?.subMenu) {
                this.#activeParentLabel = label;
                this.#openSecondary(item.subMenu);
            }
        } else {
            this.#ensureDrawer();
        }
    }

    /**
     * Collapses all expanded items in the slotted bottom menu.
     */
    #collapseBottomMenuItems() {
        const bottomMenuItems = this.shadowRoot?.querySelector('slot[name="bottom-menu"]');
        if (!bottomMenuItems) return;
        bottomMenuItems.assignedElements()
            .flatMap(el => [...el.querySelectorAll('neptune-sidenav-item')])
            .forEach(el => el.removeAttribute('expanded'));
    }

    /**
     * Handles item click events to set the active route and emit navigation event.
     * @param {CustomEvent} event - The neptune-sidenav-item-click event
     */
    #handleItemClick(event) {
        this.setActiveRoute(event.detail.route);
        const current = this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail;
        if (current !== Sidenav.SIDENAV_STATES.rail) {
            this.shadowRoot.querySelectorAll('neptune-sidenav-item[expanded]')
                .forEach(el => el.removeAttribute('expanded'));
            this.#collapseBottomMenuItems();
            this.setAttribute('state', Sidenav.SIDENAV_STATES.rail);
            this.#emitToggle(Sidenav.SIDENAV_STATES.rail);
        }
        this.dispatchEvent(new CustomEvent('neptune-sidenav-navigate', {
            bubbles: true, composed: true,
            detail: { id: event.detail.id, route: event.detail.route, label: event.detail.label }
        }));
        
        window.dispatchEvent(new CustomEvent('neptune-location-change', {
            detail: { route: event.detail.route }
        }));

    }

    /**
     * Handles location change events from browser navigation or history changes.
     * Updates the active route based on the new location.
     * @param {Event} event - The neptune-location-change event or popstate event
     */
    #handleLocationChange(event) {
        const newRoute = this.#normalizeRoute(event?.detail?.route ?? window.location.pathname);
        if (this.#activeRoute && newRoute === this.#normalizeRoute(this.#activeRoute)) return;
        this.setActiveRoute(newRoute);
    }

    /**
     * Handles drawer open events to collapse the sidenav when another drawer opens.
     */
    #handleDrawerOpen() {
        if (this.getAttribute('state') === Sidenav.SIDENAV_STATES.rail) {
            return;
        }
        this.setAttribute('state', Sidenav.SIDENAV_STATES.rail);
        this.#emitToggle(Sidenav.SIDENAV_STATES.rail);
    }

    /**
     * Opens the secondary drawer with the provided submenu items.
     * @param {Array} subMenuItems - Array of submenu item data
     */
    #openSecondary(subMenuItems) {
        this.#populateSecondaryMenu(subMenuItems);
        this.#transitionToSecondDrawer();
    }

    /**
     * Populates the secondary menu with items and restores active state if applicable.
     * @param {Array} subMenuItems - Array of submenu item data
     */
    #populateSecondaryMenu(subMenuItems) {
        const menu = this.shadowRoot.querySelector('menu:nth-of-type(2)');
        if (menu) {
            menu.innerHTML = '';
            subMenuItems.forEach(item => {
                const el = this.#createNavItem(item, false);
                if (this.#activeRoute && el.getAttribute('route') === this.#activeRoute) {
                    el.setAttribute('active', '');
                    el.setAttribute('state', 'active');
                }
                menu.appendChild(el);
            });
        }
    }

    /**
     * Transitions the sidenav to secondary drawer state and emits a toggle event.
     */
    #transitionToSecondDrawer() {
        this.setAttribute('state', Sidenav.SIDENAV_STATES.secondaryDrawer);
        this.#emitToggle(Sidenav.SIDENAV_STATES.secondaryDrawer);
    }

    /**
     * Navigates back from secondary drawer to primary drawer and clears expanded states.
     */
    #goBackToDrawer() {
        this.shadowRoot.querySelectorAll('neptune-sidenav-item[expanded]')
            .forEach(el => el.removeAttribute('expanded'));
        this.#collapseBottomMenuItems();

        this.setAttribute('state', Sidenav.SIDENAV_STATES.primaryDrawer);
        this.#emitToggle(Sidenav.SIDENAV_STATES.primaryDrawer);
    }

    /**
     * Ensures the sidenav is in at least primary-drawer state if not already.
     */
    #ensureDrawer() {
        if (this.getAttribute('state') !== Sidenav.SIDENAV_STATES.primaryDrawer) {
            this.setAttribute('state', Sidenav.SIDENAV_STATES.primaryDrawer);
            this.#emitToggle(Sidenav.SIDENAV_STATES.primaryDrawer);
        }
    }

    /**
     * Renders menu items in the primary menu based on the current menu data.
     * Items are created with appropriate collapsed state based on the current sidenav state.
     */
    #renderPrimaryMenu() {
        const menu = this.shadowRoot?.querySelector('menu:first-of-type');
        if (!menu) return;
        menu.innerHTML = '';

        const collapsed = (this.getAttribute('state') || Sidenav.SIDENAV_STATES.rail) === Sidenav.SIDENAV_STATES.rail;

        this.menuData.forEach(group => {
            const items = (group.menu && !group.legacy) ? group.menu : [group];
            items.forEach(item => menu.appendChild(this.#createNavItem(item, collapsed)));
        });
    }

    /**
     * Creates a neptune-sidenav-item element from menu item data.
     * @param {Object} item - Menu item data with properties like name, route, icon, subMenu
     * @param {boolean} collapsed - Whether the item should be in collapsed state
     * @returns {HTMLElement}
     */
    #createNavItem(item, collapsed) {
        const el = document.createElement('neptune-sidenav-item');
        const label = item.label || item.name || '';
        el.setAttribute('label', label);
        el.setAttribute('route', item.route || '#');
        if (item.icon) el.setAttribute('icon', item.icon);
        if (item.avatarText) el.setAttribute('avatarText', item.avatarText);
        if (item.id) el.setAttribute('data-item-id', item.id);
        if (item.subMenu?.length) el.setAttribute('submenu', '');
        if (collapsed) el.setAttribute('collapsed', 'true');
        if (item.isActive) el.setAttribute('active', '');
        return el;
    }

    /**
     * Finds a menu item or slotted item by label name.
     * Returns the item data with its submenu if applicable.
     * @param {string} label - The label of the menu item to find
     * @returns {Object|null}
     */
    #findMenuItem(label) {
        const flat = this.menuData.flatMap(g => g.menu || [g]);
        const fromMenu = flat.find(item => (item.label || item.name) === label);
        if (fromMenu) return fromMenu;

        const bottomMenuslot = this.shadowRoot?.querySelector('slot[name="bottom-menu"]');
        const bottomMenuSlotMatch = bottomMenuslot?.assignedElements()
            .flatMap(el => [...el.querySelectorAll('neptune-sidenav-item')])
            .find(el => (el.getAttribute('label') || el.getAttribute('name')) === label);

        if (!bottomMenuSlotMatch?.dataset.submenu) return null;
        return { subMenu: JSON.parse(bottomMenuSlotMatch.dataset.submenu) };        
    }

    /**
     * Patches the global history.pushState and history.replaceState methods to emit
     * 'neptune-location-change' events for route tracking. Only applies the patch once.
     */
    patchHistoryNavigation() {
        if (window.neptunePushStatePatchApplied) return;
        const origPush = history.pushState.bind(history);
        const origReplace = history.replaceState.bind(history);
        history.pushState = function (state, title, url) {
            origPush(state, title, url);
            window.dispatchEvent(new CustomEvent('neptune-location-change', { detail: { route: String(url) } }));
        };
        history.replaceState = function (state, title, url) {
            origReplace(state, title, url);
            window.dispatchEvent(new CustomEvent('neptune-location-change', { detail: { route: String(url) } }));
        };
        window.neptunePushStatePatchApplied = true;
    }

    /**
     * Emits a neptune-sidenav-toggle event with the provided state.
     * @param {string} state - The new sidenav state
     */
    #emitToggle(state) {
        this.dispatchEvent(new CustomEvent('neptune-sidenav-toggle', {
            bubbles: true, composed: true,
            detail: { state }
        }));
    }
}

if (!customElements.get('neptune-sidenav')) {
    customElements.define('neptune-sidenav', Sidenav);
}

export default Sidenav;